import { useState, useEffect, useMemo } from 'react';

/** Rarity id → file name (backend sometimes sends rarity_id) */
const RARITY_ID_TO_NAME = {
  1: 'basic',
  2: 'plus',
  3: 'rare',
  4: 'epic',
  5: 'unique',
};

/**
 * API/DB league name → folder name under card_images (when different).
 * e.g. DB has "Süper Lig" but folder is "SuperLig1".
 */
const LEAGUE_NAME_TO_FOLDER = {
  'Süper Lig': 'SuperLig1',
  'Super Lig': 'SuperLig1',
  'Turkish Super League': 'SuperLig1',
  'Super League': 'SuperLig1',
  'Ligue 1': 'Ligue1',   // cartella senza spazio (Lens, Lille, ecc.)
  // Serie A: su GitHub (dios10_assets) la cartella è "Serie A" (con spazio) → nessuna mappatura
  '2. Bundesliga': 'Bundesliga',  // St. Pauli etc.: card_images ha solo cartella Bundesliga
  'Major League Soccer': 'Major Soccer League',  // MLS: Cincinnati, Inter Miami, etc.
};

/**
 * API/DB team name → folder name under card_images (when different).
 * e.g. DB has "AC Milan" but folder is "Milan". Beşiktaş (ş) → Besiktas (ASCII).
 */
const TEAM_NAME_TO_FOLDER = {
  'AC Milan': 'Milan',
  'FC Internazionale': 'Inter',
  'Inter': 'Inter',
  'FC Barcelona': 'Barcellona',
  'FC Barcellona': 'Barcellona',
  'Barcelona': 'Barcellona',
  'FC Bayern München': 'Bayern Monaco',
  'Bayern Munich': 'Bayern Monaco',
  'Bayern München': 'Bayern Monaco',
  'Beşiktaş': 'Besiktas',
  'Beşiktaş JK': 'Besiktas',
  'Besiktas JK': 'Besiktas',
  'Besiktas': 'Besiktas',
  'Fenerbahçe': 'Fenerbache',
  'Fenerbahçe SK': 'Fenerbache',
  'Fenerbahce SK': 'Fenerbache',
  'Fenerbahce': 'Fenerbache',
  // Caratteri speciali / varianti comuni (allineamento con cartelle card_images)
  'Atlético Madrid': 'Atletico Madrid',
  'Paris Saint Germain': 'PSG',
  'PSG': 'PSG',  // cartella = PSG, lega = Ligue1
  // Olympique Lyonnais / Marseille: su GitHub (dios10_assets) le cartelle sono "Olympique Lyonnais", "Olympique Marseille" → nessuna mappatura
  // Brighton & Hove Albion: cartella = "Brighton & Hove Albion" (nessuna mappatura)
  // LOSC Lille: su GitHub cartella = "LOSC Lille" (non "Lille") → nessuna mappatura
  // Athletic Club: su GitHub cartella = "Athletic Club" → usare nome API così com'è (nessuna mappatura)
  // Cagliari: DB ha "Cagliari", GitHub = "Cagliari Calcio"
  'Cagliari': 'Cagliari Calcio',
  'Cagliari FC': 'Cagliari Calcio',
  // Cremonese, Parma, Sassuolo, Torino, Udinese: cartelle GitHub = Cremonese, Parma, Sassuolo, Torino, Udinese
  'US Cremonese': 'Cremonese',
  'Cremonese Calcio': 'Cremonese',
  'Parma Calcio 1913': 'Parma',
  'Parma Calcio': 'Parma',
  'US Sassuolo Calcio': 'Sassuolo',
  'Sassuolo Calcio': 'Sassuolo',
  'Torino FC': 'Torino',
  'Udinese Calcio': 'Udinese',
  // Villarreal: DB ha "Villarreal", GitHub = "Villareal" (un'r')
  'Villarreal': 'Villareal',
  'Villarreal CF': 'Villareal',
  // Celta: su GitHub (dios10_assets) cartella = "Celta de Vigo" → nessuna mappatura
  'Tottenham Hotspur': 'Totthenam',  // GitHub (dios10_assets) cartella = "Totthenam"
  'Tottenham': 'Totthenam',
  'Spurs': 'Totthenam',
  'Newcastle United': 'Newcastle',
  'Fulham': 'Fullham',           // GitHub = Fullham (doppia l)
  'Burnley': 'Burnely',          // GitHub (dios10_assets) cartella = "Burnely"
  'Burnely': 'Burnely',
  'Nottingham Forest': 'Nottingham', // GitHub = Nottingham
  "Nott'ham Forest": 'Nottingham',
  // Bundesliga: su GitHub (dios10_assets) cartelle = nomi completi (Bayer 04 Leverkusen, Eintracht Frankfurt, etc.) → nessuna mappatura riduttiva
  // 'Bayer 04 Leverkusen': 'Leverkusen',  // RIMOSSO: cartella GitHub = "Bayer 04 Leverkusen"
  // 'Eintracht Frankfurt': 'Frankfurt',    // RIMOSSO: cartella GitHub = "Eintracht Frankfurt"
  // 'TSG Hoffenheim': 'Hoffenheim',      // RIMOSSO: cartella GitHub = "TSG Hoffenheim"
  // 'VfB Stuttgart': 'Stuttgart',        // RIMOSSO: cartella GitHub = "VfB Stuttgart"
  'SC Freiburg': 'Freiburg',
  // St. Pauli, VfL Wolfsburg, Werder Bremen: cartelle GitHub = stessi nomi, varianti API sotto
  'FC St. Pauli': 'St. Pauli',
  'St Pauli': 'St. Pauli',
  'SV Werder Bremen': 'Werder Bremen',
  // Borussia Dortmund: su GitHub (dios10_assets) cartella = "Borussia Dortmund"
  'Borussia Dortmund': 'Borussia Dortmund',
  'Dortmund': 'Borussia Dortmund',
  'BVB': 'Borussia Dortmund',
  'Borussia Dortmund GmbH & Co. KGaA': 'Borussia Dortmund',
  'Siviglia': 'Sevilla',
  'Sevilla': 'Siviglia',  // cartella card_images = Siviglia
  // Lens: cartella = "Lens", nessuna mappatura
  // Real Betis: nessuna mappatura → si usa "Real Betis" come nome cartella
  'AFC Bournemouth': 'Bournemouth',
  'Bournemouth AFC': 'Bournemouth',
  'Sunderland AFC': 'Sunderland',
  'AFC Sunderland': 'Sunderland',
  // LA Galaxy: cartella GitHub = "LA Galaxy" (combacia con SportMonks), nessuna mappatura
  // Los Angeles FC: squadra diversa, cartella propria se presente su GitHub
  'Vancouver Whitecaps': 'Vancouver',
};

const getLeagueFolderName = (league) => {
  if (!league) return null;
  const trimmed = String(league).trim();
  return LEAGUE_NAME_TO_FOLDER[trimmed] ?? trimmed;
};

const getTeamFolderName = (team) => {
  if (!team) return null;
  const trimmed = String(team).trim().replace(/\s+/g, ' ');
  const exact = TEAM_NAME_TO_FOLDER[trimmed];
  if (exact) return exact;
  const lower = trimmed.toLowerCase();
  const keyMatch = Object.keys(TEAM_NAME_TO_FOLDER).find((k) => k.toLowerCase() === lower);
  if (keyMatch) return TEAM_NAME_TO_FOLDER[keyMatch];
  // Borussia Dortmund: API può inviare varianti (GmbH, spazi, etc.) → cartella GitHub è "Borussia Dortmund"
  if (lower.includes('dortmund') && (lower.includes('borussia') || lower === 'dortmund' || lower === 'bvb')) return 'Borussia Dortmund';
  return trimmed;
};

/**
 * Normalize rarity to file name (lowercase: basic, plus, rare, epic, unique).
 * Supports rarity.name, rarity.display_name, rarity_name, rarity (string), or rarity_id (number).
 * @param {Object} card - Card object
 * @returns {string|null}
 */
const getNormalizedRarity = (card) => {
  const id = card?.rarity_id ?? card?.rarity?.id;
  if (id != null && RARITY_ID_TO_NAME[Number(id)] != null) {
    return RARITY_ID_TO_NAME[Number(id)];
  }
  const raw = card?.rarity?.name || card?.rarity?.display_name || card?.rarity_name || card?.rarity || null;
  if (!raw) return null;
  return String(raw).toLowerCase().trim();
};

/** Base URL per card_images: sempre GitHub (dios10_assets). Solo REACT_APP_CARD_IMAGES_URL può sovrascrivere. Non usare il backend. */
const CARD_IMAGES_GITHUB_RAW = 'https://raw.githubusercontent.com/TokenRevolution/dios10_assets/main';
const getCardImagesBaseUrl = () => {
  const override = (process.env.REACT_APP_CARD_IMAGES_URL || '').replace(/\/+$/, '');
  return override || CARD_IMAGES_GITHUB_RAW;
};

/** trycard.png: sempre da GitHub così il fallback funziona anche se il backend non serve /card_images. */
const getTrycardFallbackUrl = () => `${CARD_IMAGES_GITHUB_RAW}/card_images/trycard.png`;

/**
 * Build custom image URL: {base}/card_images/{league}/{team}/{playerId}/{playerId}_{rarity}.{ext}
 * Vecchie card = .webp, nuove card = .png su GitHub (dios10_assets)
 */
const buildCustomImageUrl = (league, team, playerId, rarity, ext = 'webp') => {
  if (!league || !team || !playerId || !rarity) return null;
  const enc = (v) => encodeURIComponent(String(v));
  const base = getCardImagesBaseUrl();
  return `${base}/card_images/${enc(league)}/${enc(team)}/${enc(String(playerId))}/${enc(playerId + '_' + rarity + '.' + ext)}`;
};

/**
 * Get all custom image URL candidates for a card (webp + png per vecchie e nuove card).
 */
const getCustomImageUrlCandidates = (card) => {
  const leagueRaw = card?.player?.league || card?.league || null;
  const league = getLeagueFolderName(leagueRaw) ?? leagueRaw;
  const teamRaw = card?.player?.team || card?.team || null;
  const team = getTeamFolderName(teamRaw) ?? teamRaw;
  const playerId = card?.player?.id ?? card?.player_id ?? card?.playerId ?? null;
  const rarity = getNormalizedRarity(card);
  const oldLeagueRaw = card?.player?.old_league ?? null;
  const oldLeague = getLeagueFolderName(oldLeagueRaw) ?? oldLeagueRaw;
  const oldTeamRaw = card?.player?.old_team ?? null;
  const oldTeam = getTeamFolderName(oldTeamRaw) ?? oldTeamRaw;
  const urls = [];
  if (league && team && playerId && rarity) {
    urls.push(buildCustomImageUrl(league, team, playerId, rarity, 'webp'));  // vecchie card
    urls.push(buildCustomImageUrl(league, team, playerId, rarity, 'png'));    // nuove card
  }
  if (oldLeague && oldTeam && playerId && rarity) {
    urls.push(buildCustomImageUrl(oldLeague, oldTeam, playerId, rarity, 'webp'));
    urls.push(buildCustomImageUrl(oldLeague, oldTeam, playerId, rarity, 'png'));
  }
  return urls;
};

export const getCustomPlayerImageUrl = (card) => getCustomImageUrlCandidates(card)[0] ?? null;

/**
 * Get ordered image URLs for a card.
 * Prova webp e png (vecchie vs nuove card su GitHub).
 * @param {Object} card - Card object
 * @param {string} card.player?.image_path - Player image URL from SportMonks
 * @param {string} card.player?.photo_url - Player photo URL
 * @param {string} card.image_url - Card image URL
 * @returns {string[]} Image URLs in priority order
 */
export const getCardImageCandidates = (card) => {
  const base = getCardImagesBaseUrl();
  const customUrls = getCustomImageUrlCandidates(card);
  const explicitCustomImage =
    card?.customImagePath ||
    card?.custom_image_path ||
    card?.image_path ||
    null;
  const fallbackTrycard = `${base}/card_images/trycard.png`;
  const trycardGuaranteed = getTrycardFallbackUrl();
  const candidates = [
    explicitCustomImage,
    ...customUrls,
    card?.image_url,
    card?.player?.image_path,
    card?.player?.imagePath,
    card?.player?.photo_url,
    fallbackTrycard,
    trycardGuaranteed
  ];
  const filtered = candidates.filter((url, i) => url && candidates.indexOf(url) === i);
  return filtered.length > 0 ? filtered : [trycardGuaranteed];
};

/**
 * Get card image URL (first available candidate)
 * @param {Object} card - Card object
 * @returns {string} Image URL or fallback to trycard
 */
export const getCardImageUrl = (card) => {
  const candidates = getCardImageCandidates(card);
  return candidates[0] || getTrycardFallbackUrl();
};

/**
 * Card Image component: tries playerId_rarity.webp first, then fallback on load error.
 * @param {Object} props
 * @param {Object} props.card - Card object (with player, rarity, league, team)
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.alt - Alt text
 * @returns {JSX.Element}
 */
/** Stable key for this card's image (player + rarity) so we reset fallback when card changes */
const getCardImageKey = (card) => {
  const playerId = card?.player?.id ?? card?.player_id ?? card?.playerId ?? null;
  const rarity = getNormalizedRarity(card);
  return `${playerId ?? ''}-${rarity ?? ''}`;
};

export const CardImage = ({ card, className = '', alt = 'Card' }) => {
  const [fallbackIndex, setFallbackIndex] = useState(0);
  const imageKey = useMemo(() => getCardImageKey(card), [card]);
  const candidates = useMemo(() => {
    try {
      return getCardImageCandidates(card ?? {});
    } catch {
      return [getTrycardFallbackUrl()];
    }
  }, [card]);
  const imageUrl = candidates[fallbackIndex] || getTrycardFallbackUrl();
  const safeSrc = imageUrl && String(imageUrl).trim() ? imageUrl : getTrycardFallbackUrl();

  // Reset to first candidate (custom URL) when showing a different card, so each card always tries its own file first
  useEffect(() => {
    setFallbackIndex(0);
  }, [imageKey]);

  const handleError = () => {
    setFallbackIndex((current) => {
      const next = Math.min(current + 1, candidates.length - 1);
      return next;
    });
  };

  return (
    <img
      src={safeSrc}
      alt={alt}
      className={className}
      onError={handleError}
    />
  );
};

